import { useCallback } from 'react';

import type { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import ROLES from 'config/constants/ROLES';
import isPocketSafeActionTemplateStaff from 'helpers/isPocketSafeActionTemplateStaff';
import userDataStorage from 'storages/userDataStorage';

/**
 * Returns whether the current user can toggle Active/Inactive status of a template.
 * For org templates: same as canEdit (Content Editor or AL Admin).
 * For PocketSafe (Global) templates: PocketSafe staff can toggle; organization Action Library
 * Administrator can toggle (local override for their org only - backend applies org-scoped status).
 * Used for AC 4.2: AL Admin can Activate/Deactivate Global templates (local override).
 */
const useCanToggleActiveActionTemplate = () => {
  const userData = userDataStorage.get() as { ps_roles?: string[]; roles?: number[] } | undefined;
  const { ps_roles = [], roles = [] } = userData || {};

  const isOrganizationActionLibraryAdmin = roles.includes(ROLES.ORGANIZATION.ACTION_LIBRARY_ADMINISTRATOR);

  const canToggleActive = useCallback(
    (template: ActionTemplate) => {
      if (!template) return false;

      if (isPocketSafeActionTemplateStaff(ps_roles)) return true;

      if (template.ownerType !== 'pocketsafe') return true;

      if (isOrganizationActionLibraryAdmin) return true;

      return false;
    },
    [ps_roles, isOrganizationActionLibraryAdmin],
  );

  return canToggleActive;
};

export default useCanToggleActiveActionTemplate;
